import { i18n } from '@lingui/core';
import md5 from 'md5';
import { type FetchOptions, ofetch } from 'ofetch';
import type { Key, SWRConfiguration } from 'swr';
import useSWR, { useSWRConfig } from 'swr';
import type { SWRInfiniteConfiguration } from 'swr/infinite';
import useSWRInfinite from 'swr/infinite';
import type { SWRMutationConfiguration } from 'swr/mutation';
import useSWRMutation from 'swr/mutation';
import {
  getAccessToken,
  redirectToLogin,
  refreshAccessToken,
  setAccessToken,
  useParsedAccessToken,
} from '@/auth/api';
import { ACCOUNT_API_BASE_URL } from '@/base/constants';
import { withRecaptcha } from '@/base/recaptcha';

export type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

export type EndpointDef = {
  params?: Record<string, unknown>;
  query?: Record<string, unknown>;
  body?: unknown;
  response?: unknown;
};

type ValidEndpointDef<T> = T extends EndpointDef
  ? Exclude<keyof T, keyof EndpointDef> extends never
    ? T
    : never
  : never;

export type ServiceEndpoints<
  T extends {
    [K in keyof T]: K extends `${HttpMethod}:${string}`
      ? ValidEndpointDef<T[K]>
      : never;
  },
> = T;

export interface ServiceRegistry {
  _: never;
}

export type Service = keyof ServiceRegistry;

type RouteKeysOf<S extends Service> = keyof ServiceRegistry[S] & string;

export type ValidRoute = {
  [S in Service]: `${S & string}:${RouteKeysOf<S>}`;
}[Service];

type RouteService<R extends string> = R extends `${infer S}:${string}`
  ? S & Service
  : never;

type RouteKey<R extends string> = R extends `${infer _S}:${infer K}`
  ? K
  : never;

export type RouteDef<R extends ValidRoute> =
  ServiceRegistry[RouteService<R>][RouteKey<R> &
    keyof ServiceRegistry[RouteService<R>]];

export type ResponseOf<D extends ValidRoute> = RouteDef<D> extends {
  response: infer R;
}
  ? R
  : void;

export type ParamsOf<D extends ValidRoute> = RouteDef<D> extends {
  params: infer R;
}
  ? R
  : {};

type BackendResponse<R extends ValidRoute> = {
  response: ResponseOf<R>;
  cursor: string | null;
};

export type RevalidateTarget =
  | ValidRoute
  | '*'
  | `${Service & string}:*`
  | `${Service & string}:${HttpMethod}:*`
  | `${Service & string}:${HttpMethod}:${string}*`;

export type BackendConfig<
  R extends ValidRoute,
  S extends 'swr' | 'swr-infinite' | 'swr-mutation',
> = {
  enabled?: boolean;
  revalidate?: RevalidateTarget[];
  fetchOptions?: FetchOptions<'json', ResponseOf<R>>;
  recaptchaAction?: string;
  swrOptions?: S extends 'swr'
    ? SWRConfiguration<ResponseOf<R>>
    : S extends 'swr-infinite'
      ? SWRInfiniteConfiguration<BackendResponse<R>>
      : SWRMutationConfiguration<ResponseOf<R>, any, Key, ParamsOf<R>>;
};

/* ============================ Internal Methods ============================ */

const prefixes: Record<string, string> = {};
export const registerService = (name: string, prefix: string) =>
  Object.assign(prefixes, {
    [name]: prefix,
  });

const backendKeySymbol = 'backend-api';
const buildKey = (
  route: string,
  options: Record<string, unknown>,
  accessToken: ReturnType<typeof useParsedAccessToken>,
) => {
  const { cursor, ...params } = options;
  const parts: unknown[] = [backendKeySymbol, route];
  if (params) parts.push(`params:${md5(JSON.stringify(params))}`);
  parts.push(`user:${md5(JSON.stringify(accessToken))}`);
  if (cursor) parts.push(`cursor:${cursor}`);
  return parts as Key;
};

/* ========================== External Methods ============================ */
const KNOWN_BASE_URLS = [ACCOUNT_API_BASE_URL];

export const api = ofetch.create({
  headers: {
    Accept: 'application/json',
  },
  baseURL: ACCOUNT_API_BASE_URL,
  onRequest: [
    ({ options }) => {
      const accessToken = getAccessToken();
      if (accessToken && KNOWN_BASE_URLS.some((x) => options.baseURL === x)) {
        options.headers.set('Authorization', `Bearer ${accessToken}`);
      }
    },
    ({ options }) => {
      options.headers.set('Accept-Language', i18n.locale || 'en');
    },
  ],
  onResponseError: [
    async ({ response, options }) => {
      const accessToken = getAccessToken();
      if (
        response?.status === 401 &&
        KNOWN_BASE_URLS.some((x) => options.baseURL === x) &&
        !response?.url.includes('/account/auth/access-token') &&
        accessToken
      ) {
        try {
          const newAccessToken = await refreshAccessToken();
          if (!newAccessToken)
            throw new Error('Failed to refresh access token');
          setAccessToken(newAccessToken);
        } catch {
          setAccessToken();
          redirectToLogin();
        }
      }
    },
  ],
  retry: 3,
  retryDelay: 3000,
  retryStatusCodes: [408, 409, 425, 429, 500, 502, 503, 504],
});

export async function backend<R extends ValidRoute>(
  route: R,
  options: ParamsOf<R> & { cursor?: unknown },
  config?: BackendConfig<R, never>['fetchOptions'],
) {
  const [service, rawMethod, rawPath] = route.split(':');
  const prefix = prefixes[service];
  const method = rawMethod.toUpperCase();
  const { cursor, ...params } = options;

  const bodyOrQuery = params ?? {};
  const path = rawPath.replace(/\{(\w+)\}/g, (_, k: string) => {
    if (k in bodyOrQuery) {
      const replaceWith = encodeURIComponent(
        (bodyOrQuery?.[k as never] as string)?.toString() ?? '',
      );
      delete bodyOrQuery[k as never];
      return replaceWith;
    }
    return 'me';
  });

  let query: undefined | {};
  let body: undefined | {};

  if (['PUT', 'POST', 'PATCH'].includes(method)) {
    body = bodyOrQuery;
  } else {
    query = {
      ...bodyOrQuery,
      ...(cursor ? { cursor } : {}),
    };
  }

  const response = await api.raw(`${prefix}/${path}`, {
    ...config,
    method,
    query,
    body,
  });

  return {
    response: response._data,
    cursor: response.headers.get('x-cursor') ?? null,
  } as BackendResponse<R>;
}

export function useBackend<R extends ValidRoute>(
  route: R,
  options: ParamsOf<R>,
  config?: BackendConfig<R, 'swr'>,
) {
  const accessToken = useParsedAccessToken();
  return useSWR<ResponseOf<R>>(
    config?.enabled === false
      ? null
      : buildKey(route, options as never, accessToken),
    async () => {
      const result = await backend(
        route,
        options as never,
        config?.fetchOptions,
      );
      return result.response;
    },
    { ...config?.swrOptions },
  );
}

export function useBackendInfinite<R extends ValidRoute>(
  route: R,
  options: ParamsOf<R>,
  config?: BackendConfig<R, 'swr-infinite'>,
) {
  const accessToken = useParsedAccessToken();
  return useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (config?.enabled === false) return null;
      const cursor = previousPageData?.cursor;
      if (pageIndex > 0 && !cursor) return null;
      return buildKey(
        route,
        {
          ...(options ?? {}),
          cursor,
        } as never,
        accessToken,
      );
    },
    async (keys) => {
      const cursor =
        Array.isArray(keys) &&
        typeof keys[keys.length - 1] === 'string' &&
        keys[keys.length - 1].startsWith('cursor:')
          ? (keys[keys.length - 1].replace('cursor:', '') as string)
          : null;
      const result = await backend(
        route,
        {
          ...(options ?? {}),
          cursor,
        } as never,
        config?.fetchOptions,
      );
      return result;
    },
    { ...config?.swrOptions },
  );
}

export const useBackendRevalidate = () => {
  const { mutate, cache } = useSWRConfig();

  return (revalidateKeys: RevalidateTarget[]) => {
    const promises: Promise<unknown>[] = [];
    for (const cashedKey of cache.keys()) {
      for (const revalidateKey of revalidateKeys ?? []) {
        if (cashedKey.includes(revalidateKey.replace('*', ''))) {
          promises.push(mutate(cashedKey));
        }
      }
    }
    return Promise.all(promises);
  };
};

export function useBackendMutation<R extends ValidRoute>(
  route: R,
  config?: BackendConfig<R, 'swr-mutation'>,
) {
  const revalidate = useBackendRevalidate();
  const accessToken = useParsedAccessToken();

  return useSWRMutation(
    config?.enabled === false ? null : buildKey(route, {}, accessToken),
    async (_key: unknown, { arg: options }: { arg: ParamsOf<R> }) => {
      const call = (extra: object = {}) =>
        backend(
          route,
          { ...(options as object), ...extra } as never,
          config?.fetchOptions,
        );
      const result = config?.recaptchaAction
        ? await withRecaptcha(config.recaptchaAction, call)
        : await call();
      if (config?.revalidate) {
        await revalidate(config.revalidate);
      }
      return result.response;
    },
    config?.swrOptions,
  );
}
